import React, { useCallback, useMemo, useRef, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { trLocaleText, enLocaleText } from "../utils/datePickerLocale";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    IconButton,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import PlaceIcon from "@mui/icons-material/Place";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import ImageIcon from "@mui/icons-material/Image";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { tr, enUS } from "date-fns/locale";
import i18n from "i18next";

import PageHeader from "../components/PageHeader";
import ResponsiveSelect from "../components/ResponsiveSelect";

const FILTER_RESELLERS = gql`
    query FilterReseller {
        filterReseller(criteria: { deleted: false }, sortBy: { field: "name", direction: ASC }) {
            id
            name
        }
    }
`;

const FILTER_CUSTOMERS = gql`
    query FilterCustomer {
        filterCustomer(criteria: { deleted: false }, sortBy: { field: "name", direction: ASC }) {
            id
            name
        }
    }
`;

const FILTER_LOCATIONS = gql`
    query FilterLocation {
        filterLocation(criteria: { deleted: false }, sortBy: { field: "name", direction: ASC }) {
            id
            name
        }
    }
`;

const MAKMINI_DAILY_STATS = gql`
    query DeviceMakminiDailyStatistics(
        $startDate: LocalDate!
        $endDate: LocalDate!
        $resellers: [ID!]
        $customers: [ID!]
        $locations: [ID!]
    ) {
        deviceMakminiDailyStatistics(
            startDate: $startDate
            endDate: $endDate
            resellers: $resellers
            customers: $customers
            locations: $locations
        ) {
            averageTemperature
            averageHumidity
            averageConductivityA
            averageConductivityB
            averageWaterTemperatureA
            averageWaterTemperatureB
            totalFlowA
            totalFlowB
            date
        }
    }
`;

const MAKMINI_HOURLY_STATS = gql`
    query DeviceMakminiHourlyStatistics(
        $date: LocalDate!
        $resellers: [ID!]
        $customers: [ID!]
        $locations: [ID!]
    ) {
        deviceMakminiHourlyStatistics(
            date: $date
            resellers: $resellers
            customers: $customers
            locations: $locations
        ) {
            averageTemperature
            averageHumidity
            averageConductivityA
            averageConductivityB
            averageWaterTemperatureA
            averageWaterTemperatureB
            totalFlowA
            totalFlowB
            hour
        }
    }
`;

const MAKMINI_TOP_LOCATIONS = gql`
    query DeviceMakminiTopLocations(
        $startDate: LocalDate!
        $endDate: LocalDate!
        $orderBy: MakminiStatisticOrderBy!
        $asc: Boolean!
        $limit: Int!
        $resellers: [ID]
        $customers: [ID]
        $locations: [ID]
    ) {
        deviceMakminiTopLocations(
            startDate: $startDate
            endDate: $endDate
            orderBy: $orderBy
            asc: $asc
            limit: $limit
            resellers: $resellers
            customers: $customers
            locations: $locations
        ) {
            location
            locationName
            averageTemperature
            averageHumidity
            averageConductivityA
            averageConductivityB
            averageWaterTemperatureA
            averageWaterTemperatureB
            totalFlowA
            totalFlowB
        }
    }
`;

const AKIFERRA_DAILY_STATS = gql`
    query DeviceAkiferraDailyStatistics(
        $startDate: LocalDate!
        $endDate: LocalDate!
        $resellers: [ID!]
        $customers: [ID!]
        $locations: [ID!]
    ) {
        deviceAkiferraDailyStatistics(
            startDate: $startDate
            endDate: $endDate
            resellers: $resellers
            customers: $customers
            locations: $locations
        ) {
            totalRoomTempWaterVolume
            totalColdTempWaterVolume
            totalSparklingWaterVolume
            totalRoomTempWaterSmallBottleCount
            totalRoomTempWaterLargeBottleCount
            totalColdTempWaterSmallBottleCount
            totalColdTempWaterLargeBottleCount
            totalSparklingWaterSmallBottleCount
            totalSparklingWaterLargeBottleCount
            date
        }
    }
`;

const AKIFERRA_HOURLY_STATS = gql`
    query DeviceAkiferraHourlyStatistics(
        $date: LocalDate!
        $resellers: [ID!]
        $customers: [ID!]
        $locations: [ID!]
    ) {
        deviceAkiferraHourlyStatistics(
            date: $date
            resellers: $resellers
            customers: $customers
            locations: $locations
        ) {
            totalRoomTempWaterVolume
            totalColdTempWaterVolume
            totalSparklingWaterVolume
            totalRoomTempWaterSmallBottleCount
            totalRoomTempWaterLargeBottleCount
            totalColdTempWaterSmallBottleCount
            totalColdTempWaterLargeBottleCount
            totalSparklingWaterSmallBottleCount
            totalSparklingWaterLargeBottleCount
            hour
        }
    }
`;

const AKIFERRA_TOP_LOCATIONS = gql`
    query DeviceAkiferraTopLocations(
        $startDate: LocalDate!
        $endDate: LocalDate!
        $orderBy: AkiferraStatisticOrderBy!
        $asc: Boolean!
        $limit: Int!
        $resellers: [ID]
        $customers: [ID]
        $locations: [ID]
    ) {
        deviceAkiferraTopLocations(
            startDate: $startDate
            endDate: $endDate
            orderBy: $orderBy
            asc: $asc
            limit: $limit
            resellers: $resellers
            customers: $customers
            locations: $locations
        ) {
            location
            locationName
            totalRoomTempWaterVolume
            totalColdTempWaterVolume
            totalSparklingWaterVolume
            totalRoomTempWaterSmallBottleCount
            totalRoomTempWaterLargeBottleCount
            totalColdTempWaterSmallBottleCount
            totalColdTempWaterLargeBottleCount
            totalSparklingWaterSmallBottleCount
            totalSparklingWaterLargeBottleCount
        }
    }
`;


const toLocalDate = (dateValue) => {
    if (!dateValue) return "";
    const d = dateValue instanceof Date ? dateValue : new Date(dateValue);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const toArray = (value) => (value ? [value] : undefined);

const numberOrZero = (value) => (typeof value === "number" && Number.isFinite(value) ? value : 0);

const getAverageByField = (rows, field) => {
    if (!rows?.length) return 0;
    const sum = rows.reduce((acc, row) => acc + numberOrZero(row?.[field]), 0);
    return sum / rows.length;
};

const getSumByField = (rows, field) => {
    if (!rows?.length) return 0;
    return rows.reduce((acc, row) => acc + numberOrZero(row?.[field]), 0);
};

const MetricCard = ({ title, value, unit }) => (
    <Box
        sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 2,
            bgcolor: "background.paper",
            minHeight: 120,
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <QueryStatsIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {title}
            </Typography>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {value}{unit && <Typography component="span" variant="body2" sx={{ ml: 0.5, fontWeight: 500, color: "text.secondary" }}>{unit}</Typography>}
        </Typography>
    </Box>
);

const MULTI_SERIES_COLORS = ["#1976D2", "#43A047"];

const MetricTrendChart = React.forwardRef(({ labels, values, seriesLabel, datasets, chartType = "bar", overlayDatasets }, ref) => {
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const svgRef = useRef(null);

    const isMulti = datasets && datasets.length > 1;
    const effectiveDatasets = isMulti
        ? datasets
        : [{ values: values || [], label: seriesLabel || "", color: "#1976D2" }];

    React.useImperativeHandle(ref, () => ({
        getSvgElement: () => svgRef.current,
        getLabels: () => labels,
        getDatasets: () => effectiveDatasets,
        getOverlayDatasets: () => overlayDatasets,
    }));
    const hasOverlay = overlayDatasets && overlayDatasets.length > 0;

    const hasData = labels?.length && effectiveDatasets.some((ds) => ds.values?.length);
    if (!hasData) {
        return (
            <Box
                sx={{
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 3,
                    textAlign: "center",
                    color: "text.secondary",
                }}
            >
                -
            </Box>
        );
    }

    const width = 1000;
    const height = 320;
    const padding = { top: 20, right: hasOverlay ? 72 : 24, bottom: 48, left: 72 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    const allSafeValues = effectiveDatasets.map((ds) => (ds.values || []).map((v) => numberOrZero(v)));
    const maxValue = Math.max(...allSafeValues.flat(), 1);
    const yTickCount = 5;
    const yTicks = Array.from({ length: yTickCount }, (_, idx) => {
        const ratio = idx / (yTickCount - 1);
        const value = maxValue * (1 - ratio);
        const y = padding.top + chartHeight * ratio;
        return { value, y };
    });
    const compactNumber = new Intl.NumberFormat(i18n.language || "tr", {
        notation: "compact",
        maximumFractionDigits: 1,
    });
    const fullNumber = new Intl.NumberFormat(i18n.language || "tr", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

    const dataLen = allSafeValues[0]?.length || 0;
    const barGap = 8;
    const barSlot = chartWidth / dataLen;
    const totalBarWidth = Math.max(6, barSlot - barGap);
    const seriesCount = effectiveDatasets.length;
    const singleBarWidth = isMulti ? totalBarWidth / seriesCount : totalBarWidth;

    const allPoints = allSafeValues.map((safeVals) =>
        safeVals.map((value, idx) => {
            const x = padding.left + idx * barSlot + barSlot / 2;
            const y = padding.top + chartHeight - (value / maxValue) * chartHeight;
            return { x, y, value };
        })
    );

    const step = dataLen > 16 ? Math.ceil(dataLen / 8) : 1;

    const overlaySafeValues = hasOverlay ? overlayDatasets.map((ds) => (ds.values || []).map((v) => numberOrZero(v))) : [];
    const overlayMaxValue = hasOverlay ? Math.max(...overlaySafeValues.flat(), 1) : 1;
    const overlayYTicks = hasOverlay
        ? Array.from({ length: yTickCount }, (_, idx) => {
            const ratio = idx / (yTickCount - 1);
            const value = overlayMaxValue * (1 - ratio);
            const y = padding.top + chartHeight * ratio;
            return { value, y };
        })
        : [];
    const overlayPoints = overlaySafeValues.map((safeVals) =>
        safeVals.map((value, idx) => {
            const x = padding.left + idx * barSlot + barSlot / 2;
            const y = padding.top + chartHeight - (value / overlayMaxValue) * chartHeight;
            return { x, y, value };
        })
    );

    const allLegendDatasets = [...effectiveDatasets, ...(hasOverlay ? overlayDatasets : [])];

    return (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Box sx={{ minWidth: 700 }}>
                <svg ref={svgRef} viewBox={`0 0 ${width} ${height + (isMulti ? 30 : 0)}`} width="100%" preserveAspectRatio="xMinYMin meet" role="img" aria-label={effectiveDatasets.map((d) => d.label).join(" / ")}>
                    {yTicks.map((tick, idx) => (
                        <g key={`y-tick-${idx}`}>
                            <line
                                x1={padding.left}
                                y1={tick.y}
                                x2={width - padding.right}
                                y2={tick.y}
                                stroke="#E5E7EB"
                                strokeDasharray="4 4"
                            />
                            <text
                                x={padding.left - 10}
                                y={tick.y + 4}
                                fontSize="11"
                                fill="#6B7280"
                                textAnchor="end"
                            >
                                {compactNumber.format(tick.value)}
                            </text>
                        </g>
                    ))}

                    {chartType === "bar" && allSafeValues.map((safeVals, sIdx) =>
                        safeVals.map((value, idx) => {
                            const barHeight = (value / maxValue) * chartHeight;
                            const groupX = padding.left + idx * barSlot + (barSlot - totalBarWidth) / 2;
                            const x = isMulti ? groupX + sIdx * singleBarWidth : groupX;
                            const y = padding.top + chartHeight - barHeight;
                            return (
                                <rect
                                    key={`bar-${sIdx}-${idx}`}
                                    x={x}
                                    y={y}
                                    width={singleBarWidth - (isMulti ? 2 : 0)}
                                    height={barHeight}
                                    fill={effectiveDatasets[sIdx]?.color || MULTI_SERIES_COLORS[sIdx % MULTI_SERIES_COLORS.length]}
                                    rx="4"
                                    onMouseEnter={() => setHoveredIndex(idx)}
                                    onMouseLeave={() => setHoveredIndex(null)}
                                />
                            );
                        })
                    )}

                    {chartType === "line" && allPoints.map((points, sIdx) => {
                        const color = effectiveDatasets[sIdx]?.color || MULTI_SERIES_COLORS[sIdx % MULTI_SERIES_COLORS.length];
                        const linePath = points.map((p, idx) => `${idx === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
                        const areaPath = linePath + ` L ${points[points.length - 1].x} ${padding.top + chartHeight} L ${points[0].x} ${padding.top + chartHeight} Z`;
                        return (
                            <g key={`line-group-${sIdx}`}>
                                <path d={areaPath} fill={color} opacity="0.08" />
                                <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" />
                                {points.map((p, idx) => (
                                    <circle
                                        key={`dot-${sIdx}-${idx}`}
                                        cx={p.x}
                                        cy={p.y}
                                        r={hoveredIndex === idx ? 6 : 4}
                                        fill={color}
                                        stroke="#fff"
                                        strokeWidth="2"
                                        onMouseEnter={() => setHoveredIndex(idx)}
                                        onMouseLeave={() => setHoveredIndex(null)}
                                    />
                                ))}
                            </g>
                        );
                    })}


                    {hasOverlay && overlayYTicks.map((tick, idx) => (
                        <text
                            key={`y-tick-right-${idx}`}
                            x={width - padding.right + 10}
                            y={tick.y + 4}
                            fontSize="11"
                            fill="#6B7280"
                            textAnchor="start"
                        >
                            {compactNumber.format(tick.value)}
                        </text>
                    ))}

                    {hasOverlay && overlayPoints.map((points, sIdx) => {
                        const color = overlayDatasets[sIdx]?.color || MULTI_SERIES_COLORS[(effectiveDatasets.length + sIdx) % MULTI_SERIES_COLORS.length];
                        const linePath = points.map((p, idx) => `${idx === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
                        return (
                            <g key={`overlay-${sIdx}`}>
                                <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" strokeDasharray="6 3" />
                                {points.map((p, idx) => (
                                    <circle
                                        key={`overlay-dot-${sIdx}-${idx}`}
                                        cx={p.x}
                                        cy={p.y}
                                        r={hoveredIndex === idx ? 6 : 4}
                                        fill={color}
                                        stroke="#fff"
                                        strokeWidth="2"
                                        onMouseEnter={() => setHoveredIndex(idx)}
                                        onMouseLeave={() => setHoveredIndex(null)}
                                    />
                                ))}
                            </g>
                        );
                    })}

                    {labels.map((label, idx) => {
                        if (idx % step !== 0 && idx !== labels.length - 1) return null;
                        const x = padding.left + idx * barSlot + barSlot / 2;
                        return (
                            <text
                                key={`label-${idx}`}
                                x={x}
                                y={height - 14}
                                fontSize="11"
                                fill="#6B7280"
                                textAnchor="middle"
                            >
                                {label}
                            </text>
                        );
                    })}

                    {labels.map((_, idx) => (
                        <rect
                            key={`hover-zone-${idx}`}
                            x={padding.left + idx * barSlot}
                            y={padding.top}
                            width={barSlot}
                            height={chartHeight}
                            fill="transparent"
                            onMouseEnter={() => setHoveredIndex(idx)}
                            onMouseLeave={() => setHoveredIndex(null)}
                        />
                    ))}

                    {hoveredIndex !== null && (() => {
                        const tooltipRows = [];
                        allPoints.forEach((points, sIdx) => {
                            const p = points[hoveredIndex];
                            if (p) tooltipRows.push({ label: effectiveDatasets[sIdx]?.label, value: p.value, color: effectiveDatasets[sIdx]?.color || MULTI_SERIES_COLORS[sIdx % MULTI_SERIES_COLORS.length], dashed: false });
                        });
                        if (hasOverlay) overlayPoints.forEach((points, sIdx) => {
                            const p = points[hoveredIndex];
                            if (p) tooltipRows.push({ label: overlayDatasets[sIdx]?.label, value: p.value, color: overlayDatasets[sIdx]?.color || MULTI_SERIES_COLORS[(effectiveDatasets.length + sIdx) % MULTI_SERIES_COLORS.length], dashed: true });
                        });
                        if (tooltipRows.length === 0) return null;
                        const showColor = tooltipRows.length > 1;
                        const rowHeight = 22;
                        const headerHeight = 24;
                        const tooltipHeight = headerHeight + tooltipRows.length * rowHeight + 6;
                        const tooltipWidth = 200;
                        const anchorX = padding.left + hoveredIndex * barSlot + barSlot / 2;
                        const tooltipX = Math.max(padding.left, Math.min(anchorX - tooltipWidth / 2, width - padding.right - tooltipWidth));
                        const tooltipY = Math.max(4, padding.top - tooltipHeight - 8);
                        return (
                            <g pointerEvents="none">
                                <line x1={anchorX} y1={padding.top} x2={anchorX} y2={padding.top + chartHeight} stroke="#9CA3AF" strokeWidth="1" strokeDasharray="4 3" />
                                <rect x={tooltipX} y={tooltipY} width={tooltipWidth} height={tooltipHeight} rx="8" fill="#111827" opacity="0.95" />
                                <text x={tooltipX + tooltipWidth / 2} y={tooltipY + 16} fontSize="11" fill="#9CA3AF" textAnchor="middle" fontWeight="600">
                                    {labels[hoveredIndex]}
                                </text>
                                {tooltipRows.map((row, rIdx) => {
                                    const ry = tooltipY + headerHeight + rIdx * rowHeight + 14;
                                    return (
                                        <g key={`tt-row-${rIdx}`}>
                                            {showColor && (
                                                row.dashed
                                                    ? <line x1={tooltipX + 8} y1={ry - 4} x2={tooltipX + 20} y2={ry - 4} stroke={row.color} strokeWidth="2.5" strokeDasharray="4 2" />
                                                    : <rect x={tooltipX + 8} y={ry - 8} width="12" height="8" rx="2" fill={row.color} />
                                            )}
                                            <text x={tooltipX + (showColor ? 28 : 10)} y={ry} fontSize="11" fill="#D1D5DB">
                                                {row.label}
                                            </text>
                                            <text x={tooltipX + tooltipWidth - 10} y={ry} fontSize="11" fill="#FFFFFF" textAnchor="end" fontWeight="600">
                                                {fullNumber.format(row.value)}
                                            </text>
                                        </g>
                                    );
                                })}
                            </g>
                        );
                    })()}

                    {(isMulti || hasOverlay) && allLegendDatasets.map((ds, sIdx) => {
                        const legendX = padding.left + sIdx * 160;
                        const legendY = height + 10;
                        const color = ds.color || MULTI_SERIES_COLORS[sIdx % MULTI_SERIES_COLORS.length];
                        const isDashed = sIdx >= effectiveDatasets.length;
                        return (
                            <g key={`legend-${sIdx}`}>
                                {isDashed ? (
                                    <line x1={legendX} y1={legendY + 6} x2={legendX + 12} y2={legendY + 6} stroke={color} strokeWidth="2.5" strokeDasharray="4 2" />
                                ) : (
                                    <rect x={legendX} y={legendY} width="12" height="12" rx="2" fill={color} />
                                )}
                                <text x={legendX + 18} y={legendY + 10} fontSize="12" fill="#374151">{ds.label}</text>
                            </g>
                        );
                    })}
                </svg>
            </Box>
        </Box>
    );
});

export default function Dashboard() {
    const { t } = useTranslation();
    const role = localStorage.getItem("role") || sessionStorage.getItem("role");
    const isSmall = useMediaQuery("(max-width:600px)");

    const [tab, setTab] = useState("MAKMINI");
    const defaultStartDate = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() - 9);
        return d;
    }, []);
    const [draftFilters, setDraftFilters] = useState({
        view: "DAILY",
        reseller: "",
        customer: "",
        location: "",
        startDate: defaultStartDate,
        endDate: new Date(),
    });
    const [filters, setFilters] = useState({
        view: "DAILY",
        reseller: "",
        customer: "",
        location: "",
        startDate: defaultStartDate,
        endDate: new Date(),
    });
    const [topOrderBy, setTopOrderBy] = useState({
        makmini: "FLOW_A",
        akiferra: "ROOM_TEMP_WATER_VOLUME",
    });
    const [topAsc, setTopAsc] = useState(false);
    const [topLimit, setTopLimit] = useState(5);
    const [chartMetric, setChartMetric] = useState({
        makmini: "flowAB",
        akiferra: "totalRoomTempWaterVolume",
    });
    const [flowOverlay, setFlowOverlay] = useState("ec");
    const chartRef = useRef(null);

    const handleExportPNG = useCallback(() => {
        const api = chartRef.current;
        if (!api) return;
        const svgEl = api.getSvgElement();
        if (!svgEl) return;
        const svgData = new XMLSerializer().serializeToString(svgEl);
        const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(svgBlob);
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = 2000;
            canvas.height = 700;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#FFFFFF";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            URL.revokeObjectURL(url);
            const a = document.createElement("a");
            a.download = "chart.png";
            a.href = canvas.toDataURL("image/png");
            a.click();
        };
        img.src = url;
    }, []);

    const handleExportCSV = useCallback(() => {
        const api = chartRef.current;
        if (!api) return;
        const lbls = api.getLabels();
        const ds = api.getDatasets();
        const ovr = api.getOverlayDatasets();
        const allDs = [...(ds || []), ...(ovr || [])];
        const header = ["", ...allDs.map((d) => d.label)].join(",");
        const rows = (lbls || []).map((label, idx) =>
            [label, ...allDs.map((d) => d.values?.[idx] ?? "")].join(",")
        );
        const csv = [header, ...rows].join("\n");
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
        const a = document.createElement("a");
        a.download = "chart.csv";
        a.href = URL.createObjectURL(blob);
        a.click();
        URL.revokeObjectURL(a.href);
    }, []);

    const selectedStartDate = useMemo(() => toLocalDate(filters.startDate), [filters.startDate]);
    const selectedEndDate = useMemo(() => toLocalDate(filters.endDate), [filters.endDate]);
    const [effectiveStartDate, effectiveEndDate] = useMemo(() => {
        if (!selectedStartDate && !selectedEndDate) return ["", ""];
        if (!selectedStartDate) return [selectedEndDate, selectedEndDate];
        if (!selectedEndDate) return [selectedStartDate, selectedStartDate];
        return selectedStartDate <= selectedEndDate
            ? [selectedStartDate, selectedEndDate]
            : [selectedEndDate, selectedStartDate];
    }, [selectedStartDate, selectedEndDate]);

    const statsVariables = useMemo(() => {
        const common = {
            resellers: toArray(filters.reseller),
            customers: toArray(filters.customer),
            locations: toArray(filters.location),
        };

        if (filters.view === "HOURLY") {
            return { date: effectiveStartDate, ...common };
        }

        return {
            startDate: effectiveStartDate,
            endDate: effectiveEndDate,
            ...common,
        };
    }, [filters, effectiveStartDate, effectiveEndDate]);

    const baseTopVariables = useMemo(
        () => ({
            startDate: effectiveStartDate,
            endDate: effectiveEndDate,
            asc: topAsc,
            limit: topLimit,
            resellers: toArray(filters.reseller),
            customers: toArray(filters.customer),
            locations: toArray(filters.location),
        }),
        [filters, effectiveStartDate, effectiveEndDate, topAsc, topLimit]
    );
    const makminiTopVariables = useMemo(
        () => ({ ...baseTopVariables, orderBy: topOrderBy.makmini }),
        [baseTopVariables, topOrderBy.makmini]
    );
    const akiferraTopVariables = useMemo(
        () => ({ ...baseTopVariables, orderBy: topOrderBy.akiferra }),
        [baseTopVariables, topOrderBy.akiferra]
    );

    const { data: resellerData } = useQuery(FILTER_RESELLERS, { fetchPolicy: "no-cache" });
    const { data: customerData } = useQuery(FILTER_CUSTOMERS, { fetchPolicy: "no-cache" });
    const { data: locationData } = useQuery(FILTER_LOCATIONS, { fetchPolicy: "no-cache" });

    const isMakmini = tab === "MAKMINI";
    const isHourly = filters.view === "HOURLY";

    const {
        data: makminiStatsData,
        loading: makminiStatsLoading,
        error: makminiStatsError,
        refetch: refetchMakminiStats,
    } = useQuery(isHourly ? MAKMINI_HOURLY_STATS : MAKMINI_DAILY_STATS, {
        variables: statsVariables,
        skip: !isMakmini || !effectiveStartDate || !effectiveEndDate,
        fetchPolicy: "no-cache",
    });

    const {
        data: akiferraStatsData,
        loading: akiferraStatsLoading,
        error: akiferraStatsError,
        refetch: refetchAkiferraStats,
    } = useQuery(isHourly ? AKIFERRA_HOURLY_STATS : AKIFERRA_DAILY_STATS, {
        variables: statsVariables,
        skip: isMakmini || !effectiveStartDate || !effectiveEndDate,
        fetchPolicy: "no-cache",
    });

    const {
        data: makminiTopData,
        loading: makminiTopLoading,
        error: makminiTopError,
        refetch: refetchMakminiTop,
    } = useQuery(MAKMINI_TOP_LOCATIONS, {
        variables: makminiTopVariables,
        skip: !isMakmini || !effectiveStartDate || !effectiveEndDate,
        fetchPolicy: "no-cache",
    });

    const {
        data: akiferraTopData,
        loading: akiferraTopLoading,
        error: akiferraTopError,
        refetch: refetchAkiferraTop,
    } = useQuery(AKIFERRA_TOP_LOCATIONS, {
        variables: akiferraTopVariables,
        skip: isMakmini || !effectiveStartDate || !effectiveEndDate,
        fetchPolicy: "no-cache",
    });

    const statsRows = useMemo(
        () =>
            isMakmini
                ? (makminiStatsData?.deviceMakminiHourlyStatistics ||
                  makminiStatsData?.deviceMakminiDailyStatistics ||
                  [])
                : (akiferraStatsData?.deviceAkiferraHourlyStatistics ||
                  akiferraStatsData?.deviceAkiferraDailyStatistics ||
                  []),
        [isMakmini, makminiStatsData, akiferraStatsData]
    );

    const topRows = useMemo(
        () => (isMakmini ? (makminiTopData?.deviceMakminiTopLocations || []) : (akiferraTopData?.deviceAkiferraTopLocations || [])),
        [isMakmini, makminiTopData, akiferraTopData]
    );

    const metricDefs = useMemo(() => {
        if (isMakmini) {
            return [
                { field: "averageTemperature", label: t("devicePageMakmini.columns.temperature"), unit: "°C" },
                { field: "averageHumidity", label: t("devicePageMakmini.columns.humidity"), unit: "%" },
                { field: "averageConductivityA", label: t("devicePageMakmini.columns.conductivityA"), unit: "μS" },
                { field: "averageConductivityB", label: t("devicePageMakmini.columns.conductivityB"), unit: "μS" },
                { field: "averageWaterTemperatureA", label: t("devicePageMakmini.columns.waterTempA"), unit: "°C" },
                { field: "averageWaterTemperatureB", label: t("devicePageMakmini.columns.waterTempB"), unit: "°C" },
                { field: "totalFlowA", label: t("devicePageMakmini.columns.flowA"), unit: "L", aggregation: "sum" },
                { field: "totalFlowB", label: t("devicePageMakmini.columns.flowB"), unit: "L", aggregation: "sum" },
            ];
        }

        return [
            { field: "totalRoomTempWaterVolume", label: t("stats.roomTempVol"), unit: "L", divider: 1000, aggregation: "sum" },
            { field: "totalColdTempWaterVolume", label: t("stats.coldVol"), unit: "L", divider: 1000, aggregation: "sum" },
            { field: "totalSparklingWaterVolume", label: t("stats.sparklingVol"), unit: "L", divider: 1000, aggregation: "sum" },
            { field: "totalRoomTempWaterSmallBottleCount", label: t("stats.roomSmallBottles"), aggregation: "sum" },
            { field: "totalRoomTempWaterLargeBottleCount", label: t("stats.roomLargeBottles"), aggregation: "sum" },
            { field: "totalColdTempWaterSmallBottleCount", label: t("stats.coldSmallBottles"), aggregation: "sum" },
            { field: "totalColdTempWaterLargeBottleCount", label: t("stats.coldLargeBottles"), aggregation: "sum" },
            { field: "totalSparklingWaterSmallBottleCount", label: t("stats.sparkSmallBottles"), aggregation: "sum" },
            { field: "totalSparklingWaterLargeBottleCount", label: t("stats.sparkLargeBottles"), aggregation: "sum" },
        ];
    }, [isMakmini, t]);
    const currentChartMetric = isMakmini ? chartMetric.makmini : chartMetric.akiferra;

    const allMakminiTopOrderByOptions = useMemo(
        () => [
            { value: "TEMPERATURE", label: t("devicePageMakmini.columns.temperature") },
            { value: "HUMIDITY", label: t("devicePageMakmini.columns.humidity") },
            { value: "CONDUCTIVITY_A", label: t("devicePageMakmini.columns.conductivityA") },
            { value: "CONDUCTIVITY_B", label: t("devicePageMakmini.columns.conductivityB") },
            { value: "WATER_TEMPERATURE_A", label: t("devicePageMakmini.columns.waterTempA") },
            { value: "WATER_TEMPERATURE_B", label: t("devicePageMakmini.columns.waterTempB") },
            { value: "FLOW_A", label: t("devicePageMakmini.columns.flowA") },
            { value: "FLOW_B", label: t("devicePageMakmini.columns.flowB") },
        ],
        [t]
    );
    const allAkiferraTopOrderByOptions = useMemo(
        () => [
            { value: "ROOM_TEMP_WATER_VOLUME", label: t("stats.roomTempVol") },
            { value: "COLD_TEMP_WATER_VOLUME", label: t("stats.coldVol") },
            { value: "SPARKLING_WATER_VOLUME", label: t("stats.sparklingVol") },
            { value: "ROOM_TEMP_WATER_SMALL_BOTTLE_COUNT", label: t("stats.roomSmallBottles") },
            { value: "ROOM_TEMP_WATER_LARGE_BOTTLE_COUNT", label: t("stats.roomLargeBottles") },
            { value: "COLD_TEMP_WATER_SMALL_BOTTLE_COUNT", label: t("stats.coldSmallBottles") },
            { value: "COLD_TEMP_WATER_LARGE_BOTTLE_COUNT", label: t("stats.coldLargeBottles") },
            { value: "SPARKLING_WATER_SMALL_BOTTLE_COUNT", label: t("stats.sparkSmallBottles") },
            { value: "SPARKLING_WATER_LARGE_BOTTLE_COUNT", label: t("stats.sparkLargeBottles") },
        ],
        [t]
    );
    const makminiTopOrderFieldMap = useMemo(
        () => ({
            TEMPERATURE: "averageTemperature",
            HUMIDITY: "averageHumidity",
            CONDUCTIVITY_A: "averageConductivityA",
            CONDUCTIVITY_B: "averageConductivityB",
            WATER_TEMPERATURE_A: "averageWaterTemperatureA",
            WATER_TEMPERATURE_B: "averageWaterTemperatureB",
            FLOW_A: "totalFlowA",
            FLOW_B: "totalFlowB",
        }),
        []
    );
    const akiferraTopOrderFieldMap = useMemo(
        () => ({
            ROOM_TEMP_WATER_VOLUME: "totalRoomTempWaterVolume",
            COLD_TEMP_WATER_VOLUME: "totalColdTempWaterVolume",
            SPARKLING_WATER_VOLUME: "totalSparklingWaterVolume",
            ROOM_TEMP_WATER_SMALL_BOTTLE_COUNT: "totalRoomTempWaterSmallBottleCount",
            ROOM_TEMP_WATER_LARGE_BOTTLE_COUNT: "totalRoomTempWaterLargeBottleCount",
            COLD_TEMP_WATER_SMALL_BOTTLE_COUNT: "totalColdTempWaterSmallBottleCount",
            COLD_TEMP_WATER_LARGE_BOTTLE_COUNT: "totalColdTempWaterLargeBottleCount",
            SPARKLING_WATER_SMALL_BOTTLE_COUNT: "totalSparklingWaterSmallBottleCount",
            SPARKLING_WATER_LARGE_BOTTLE_COUNT: "totalSparklingWaterLargeBottleCount",
        }),
        []
    );

    const formatter = new Intl.NumberFormat(i18n.language || "tr", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

    const averagedMetrics = useMemo(
        () =>
            metricDefs.map((metric) => {
                const allNull = statsRows.length > 0 && statsRows.every((row) => row?.[metric.field] == null);
                if (allNull) return { ...metric, average: null };
                const raw = metric.aggregation === "sum"
                    ? getSumByField(statsRows, metric.field)
                    : getAverageByField(statsRows, metric.field);
                return { ...metric, average: metric.divider ? raw / metric.divider : raw };
            }),
        [metricDefs, statsRows]
    );
    const pairedFields = new Set([
        "totalFlowA", "totalFlowB",
        "averageConductivityA", "averageConductivityB",
        "averageWaterTemperatureA", "averageWaterTemperatureB",
        "averageTemperature", "averageHumidity",
    ]);
    const chartMetricOptions = useMemo(() => {
            const base = averagedMetrics
                .filter((m) => m.average !== null && !(isMakmini && pairedFields.has(m.field)))
                .map((m) => ({
                    value: m.field,
                    label: m.unit && !m.label.includes("(") ? `${m.label} (${m.unit})` : m.label,
                }));
            if (isMakmini) {
                const hasFlowA = averagedMetrics.some((m) => m.field === "totalFlowA" && m.average !== null);
                const hasFlowB = averagedMetrics.some((m) => m.field === "totalFlowB" && m.average !== null);
                if (hasFlowA && hasFlowB) base.push({ value: "flowAB", label: `${t("devicePageMakmini.columns.flowA")} + ${t("devicePageMakmini.columns.flowB")} (L)` });
                const hasConductivityA = averagedMetrics.some((m) => m.field === "averageConductivityA" && m.average !== null);
                const hasConductivityB = averagedMetrics.some((m) => m.field === "averageConductivityB" && m.average !== null);
                if (hasConductivityA && hasConductivityB) base.push({ value: "conductivityAB", label: `${t("devicePageMakmini.columns.conductivityA")} + ${t("devicePageMakmini.columns.conductivityB")} (μS)` });
                const hasWaterTempA = averagedMetrics.some((m) => m.field === "averageWaterTemperatureA" && m.average !== null);
                const hasWaterTempB = averagedMetrics.some((m) => m.field === "averageWaterTemperatureB" && m.average !== null);
                if (hasWaterTempA && hasWaterTempB) base.push({ value: "waterTempAB", label: `${t("devicePageMakmini.columns.waterTempA")} + ${t("devicePageMakmini.columns.waterTempB")} (°C)` });
                const hasTemp = averagedMetrics.some((m) => m.field === "averageTemperature" && m.average !== null);
                const hasHumidity = averagedMetrics.some((m) => m.field === "averageHumidity" && m.average !== null);
                if (hasTemp && hasHumidity) base.push({ value: "tempHumidity", label: `${t("devicePageMakmini.columns.temperature")} + ${t("devicePageMakmini.columns.humidity")}` });
            }
            return base;
        },
        [averagedMetrics, isMakmini, t]
    );
    const nonNullFields = useMemo(
        () => new Set(averagedMetrics.filter((m) => m.average !== null).map((m) => m.field)),
        [averagedMetrics]
    );
    const addUnitToOptions = (options, fieldMap) => options.map((opt) => {
        const unit = metricDefs.find((m) => m.field === fieldMap[opt.value])?.unit;
        return unit && !opt.label.includes("(") ? { ...opt, label: `${opt.label} (${unit})` } : opt;
    });
    const makminiTopOrderByOptions = useMemo(
        () => addUnitToOptions(allMakminiTopOrderByOptions, makminiTopOrderFieldMap),
        [allMakminiTopOrderByOptions, makminiTopOrderFieldMap, metricDefs]
    );
    const akiferraTopOrderByOptions = useMemo(
        () => addUnitToOptions(
            allAkiferraTopOrderByOptions.filter((opt) => nonNullFields.has(akiferraTopOrderFieldMap[opt.value])),
            akiferraTopOrderFieldMap
        ),
        [allAkiferraTopOrderByOptions, nonNullFields, akiferraTopOrderFieldMap, metricDefs]
    );
    const isFlowAB = isMakmini && currentChartMetric === "flowAB";
    const isConductivityAB = isMakmini && currentChartMetric === "conductivityAB";
    const isWaterTempAB = isMakmini && currentChartMetric === "waterTempAB";
    const isTempHumidity = isMakmini && currentChartMetric === "tempHumidity";
    const isMultiMetric = isFlowAB || isConductivityAB || isWaterTempAB || isTempHumidity;
    const selectedChartMetric = useMemo(() => {
        if (isFlowAB) return { field: "flowAB", label: `${t("devicePageMakmini.columns.flowA")} + ${t("devicePageMakmini.columns.flowB")}` };
        if (isConductivityAB) return { field: "conductivityAB", label: `${t("devicePageMakmini.columns.conductivityA")} + ${t("devicePageMakmini.columns.conductivityB")}` };
        if (isWaterTempAB) return { field: "waterTempAB", label: `${t("devicePageMakmini.columns.waterTempA")} + ${t("devicePageMakmini.columns.waterTempB")}` };
        if (isTempHumidity) return { field: "tempHumidity", label: `${t("devicePageMakmini.columns.temperature")} + ${t("devicePageMakmini.columns.humidity")}` };
        const validFields = averagedMetrics.filter((m) => m.average !== null);
        return validFields.find((m) => m.field === currentChartMetric) || validFields[0];
    }, [averagedMetrics, currentChartMetric, isFlowAB, isConductivityAB, isWaterTempAB, isTempHumidity, t]);
    const effectiveTopOrderBy = useMemo(() => {
        if (isMakmini) return topOrderBy.makmini;
        const currentVal = topOrderBy.akiferra;
        if (akiferraTopOrderByOptions.some((opt) => opt.value === currentVal)) return currentVal;
        return akiferraTopOrderByOptions[0]?.value || currentVal;
    }, [isMakmini, topOrderBy, akiferraTopOrderByOptions]);
    const chartRows = useMemo(() => {
        const copied = [...statsRows];
        if (isHourly) {
            copied.sort((a, b) => numberOrZero(a?.hour) - numberOrZero(b?.hour));
            return copied;
        }
        copied.sort((a, b) => String(a?.date || "").localeCompare(String(b?.date || "")));
        return copied;
    }, [statsRows, isHourly]);
    const chartLabels = useMemo(
        () =>
            chartRows.map((row) => {
                if (isHourly) {
                    const h = String(numberOrZero(row?.hour)).padStart(2, "0");
                    return `${h}:00`;
                }
                return row?.date || "-";
            }),
        [chartRows, isHourly]
    );
    const chartValues = useMemo(
        () => {
            if (isMultiMetric) return [];
            const d = selectedChartMetric?.divider;
            return chartRows.map((row) => {
                const raw = numberOrZero(row?.[selectedChartMetric?.field]);
                return d ? raw / d : raw;
            });
        },
        [chartRows, selectedChartMetric, isMultiMetric]
    );
    const chartDatasets = useMemo(() => {
        if (!isMultiMetric) return null;
        if (isFlowAB) {
            return [
                { values: chartRows.map((row) => numberOrZero(row?.totalFlowA)), label: t("devicePageMakmini.columns.flowA"), color: "#1976D2" },
                { values: chartRows.map((row) => numberOrZero(row?.totalFlowB)), label: t("devicePageMakmini.columns.flowB"), color: "#43A047" },
            ];
        }
        if (isConductivityAB) {
            return [
                { values: chartRows.map((row) => numberOrZero(row?.averageConductivityA)), label: t("devicePageMakmini.columns.conductivityA"), color: "#1976D2" },
                { values: chartRows.map((row) => numberOrZero(row?.averageConductivityB)), label: t("devicePageMakmini.columns.conductivityB"), color: "#43A047" },
            ];
        }
        if (isWaterTempAB) {
            return [
                { values: chartRows.map((row) => numberOrZero(row?.averageWaterTemperatureA)), label: t("devicePageMakmini.columns.waterTempA"), color: "#1976D2" },
                { values: chartRows.map((row) => numberOrZero(row?.averageWaterTemperatureB)), label: t("devicePageMakmini.columns.waterTempB"), color: "#43A047" },
            ];
        }
        return [
            { values: chartRows.map((row) => numberOrZero(row?.averageTemperature)), label: t("devicePageMakmini.columns.temperature"), color: "#1976D2" },
            { values: chartRows.map((row) => numberOrZero(row?.averageHumidity)), label: t("devicePageMakmini.columns.humidity"), color: "#43A047" },
        ];
    }, [chartRows, isMultiMetric, isFlowAB, isConductivityAB, isWaterTempAB, t]);

    const flowOverlayDatasets = useMemo(() => {
        if (!isFlowAB || !flowOverlay) return null;
        if (flowOverlay === "ec") return [
            { values: chartRows.map((row) => numberOrZero(row?.averageConductivityA)), label: t("devicePageMakmini.columns.conductivityA"), color: "#E53935" },
            { values: chartRows.map((row) => numberOrZero(row?.averageConductivityB)), label: t("devicePageMakmini.columns.conductivityB"), color: "#FB8C00" },
        ];
        if (flowOverlay === "waterTemp") return [
            { values: chartRows.map((row) => numberOrZero(row?.averageWaterTemperatureA)), label: t("devicePageMakmini.columns.waterTempA"), color: "#7B1FA2" },
            { values: chartRows.map((row) => numberOrZero(row?.averageWaterTemperatureB)), label: t("devicePageMakmini.columns.waterTempB"), color: "#00897B" },
        ];
        return null;
    }, [chartRows, isFlowAB, flowOverlay, t]);

    const loading = isMakmini ? makminiStatsLoading : akiferraStatsLoading;
    const topLoading = isMakmini ? makminiTopLoading : akiferraTopLoading;
    const error = isMakmini ? makminiStatsError : akiferraStatsError;
    const topError = isMakmini ? makminiTopError : akiferraTopError;

    const applyFilters = () => {
        setFilters(draftFilters);
    };

    const resetFilters = () => {
        const next = {
            view: "DAILY",
            reseller: "",
            customer: "",
            location: "",
            startDate: new Date(),
            endDate: new Date(),
        };
        setDraftFilters(next);
        setFilters(next);
    };

    const handleRefresh = () => {
        if (isMakmini) {
            refetchMakminiStats();
            refetchMakminiTop();
            return;
        }
        refetchAkiferraStats();
        refetchAkiferraTop();
    };

    const topValueField = isMakmini
        ? makminiTopOrderFieldMap[topOrderBy.makmini] || "totalFlowA"
        : akiferraTopOrderFieldMap[effectiveTopOrderBy] || "totalRoomTempWaterVolume";
    const topValueDivider = metricDefs.find((m) => m.field === topValueField)?.divider;

    return (
        <Box>
            <PageHeader
                title={t("dashboard.title")}
                subtitle={t("dashboard.subtitle")}
                icon={<DashboardIcon />}
            />

            <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden", mb: 2 }}>
                <Toolbar
                    sx={{
                        px: 2,
                        py: 1,
                        gap: 1,
                        flexWrap: "wrap",
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                        bgcolor: "background.paper",
                    }}
                >
                    <ResponsiveSelect
                        label={t("dashboard.filters.view")}
                        value={draftFilters.view}
                        onChange={(e) => setDraftFilters((prev) => ({ ...prev, view: e.target.value }))}
                        options={[
                            { value: "HOURLY", label: t("dashboard.views.hourly") },
                            { value: "DAILY", label: t("dashboard.views.daily") },
                        ]}
                        isSmall={isSmall}
                    />

                    {role !== "CUSTOMER" && role !== "RESELLER" && (
                        <ResponsiveSelect
                            label={t("statisticPage.fields.reseller")}
                            value={draftFilters.reseller}
                            onChange={(e) => setDraftFilters((prev) => ({ ...prev, reseller: e.target.value }))}
                            onClear={() => setDraftFilters((prev) => ({ ...prev, reseller: "" }))}
                            data={resellerData}
                            dataKey="filterReseller"
                            t={t}
                            isSmall={isSmall}
                        />
                    )}

                    {role !== "CUSTOMER" && (
                        <ResponsiveSelect
                            label={t("statisticPage.fields.customer")}
                            value={draftFilters.customer}
                            onChange={(e) => setDraftFilters((prev) => ({ ...prev, customer: e.target.value }))}
                            onClear={() => setDraftFilters((prev) => ({ ...prev, customer: "" }))}
                            data={customerData}
                            dataKey="filterCustomer"
                            t={t}
                            isSmall={isSmall}
                        />
                    )}

                    <ResponsiveSelect
                        label={t("statisticPage.fields.location")}
                        value={draftFilters.location}
                        onChange={(e) => setDraftFilters((prev) => ({ ...prev, location: e.target.value }))}
                        onClear={() => setDraftFilters((prev) => ({ ...prev, location: "" }))}
                        data={locationData}
                        dataKey="filterLocation"
                        t={t}
                        isSmall={isSmall}
                    />

                    <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={i18n.language.startsWith("tr") ? tr : enUS}
                        localeText={i18n.language.startsWith("tr") ? trLocaleText : enLocaleText}
                    >
                        {draftFilters.view === "HOURLY" ? (
                            <DatePicker
                                label={t("dashboard.filters.date")}
                                value={draftFilters.startDate}
                                onChange={(newDate) =>
                                    setDraftFilters((prev) => ({ ...prev, startDate: newDate || new Date() }))
                                }
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        sx: { width: isSmall ? "100%" : 180 },
                                    },
                                }}
                            />
                        ) : (
                            <>
                                <DatePicker
                                    label={t("statisticPage.fields.startDate")}
                                    value={draftFilters.startDate}
                                    onChange={(newDate) =>
                                        setDraftFilters((prev) => ({ ...prev, startDate: newDate || new Date() }))
                                    }
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            sx: { width: isSmall ? "100%" : 180 },
                                        },
                                    }}
                                />
                                <DatePicker
                                    label={t("statisticPage.fields.endDate")}
                                    value={draftFilters.endDate}
                                    onChange={(newDate) =>
                                        setDraftFilters((prev) => ({ ...prev, endDate: newDate || new Date() }))
                                    }
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            sx: { width: isSmall ? "100%" : 180 },
                                        },
                                    }}
                                />
                            </>
                        )}
                    </LocalizationProvider>

                    <Button
                        variant="contained"
                        startIcon={<SearchIcon />}
                        onClick={applyFilters}
                        sx={{ height: 40, minWidth: isSmall ? "100%" : "auto" }}
                    >
                        {t("common.search")}
                    </Button>

                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<ClearIcon />}
                        onClick={resetFilters}
                        sx={{ height: 40, minWidth: isSmall ? "100%" : "auto" }}
                    >
                        {t("common.clear")}
                    </Button>

                    <Tooltip title={t("common.refresh")}>
                        <IconButton onClick={handleRefresh} aria-label="refresh">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Toolbar>
            </Paper>

            <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
                <Tabs
                    value={tab}
                    onChange={(_e, next) => setTab(next)}
                    sx={{ px: 2, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
                >
                    <Tab label={t("menu.makmini")} value="MAKMINI" />
                    <Tab label={t("menu.akiferra")} value="AKIFERRA" />
                </Tabs>

                <Box sx={{ p: 2 }}>
                    {loading ? (
                        <Box sx={{ p: 4, textAlign: "center" }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error">{error.message}</Alert>
                    ) : (
                        <>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
                                    gap: 2,
                                    mb: 3,
                                }}
                            >
                                {averagedMetrics.filter((metric) => metric.average !== null).map((metric) => (
                                    <MetricCard
                                        key={metric.field}
                                        title={metric.label}
                                        value={formatter.format(metric.average)}
                                        unit={metric.unit}
                                    />
                                ))}
                            </Box>

                            <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", p: 2, mb: 3, overflow: "hidden" }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        flexWrap: "wrap",
                                        mb: 1,
                                    }}
                                >
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        {t("dashboard.sections.trendChart")}
                                    </Typography>
                                    {isFlowAB && (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={flowOverlay === "ec"}
                                                        onChange={(e) => setFlowOverlay(e.target.checked ? "ec" : "")}
                                                        size="small"
                                                    />
                                                }
                                                label={t("mapPage.conductivity")}
                                                componentsProps={{ typography: { variant: "body2", fontWeight: 500 } }}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={flowOverlay === "waterTemp"}
                                                        onChange={(e) => setFlowOverlay(e.target.checked ? "waterTemp" : "")}
                                                        size="small"
                                                    />
                                                }
                                                label={t("mapPage.waterTemperature")}
                                                componentsProps={{ typography: { variant: "body2", fontWeight: 500 } }}
                                            />
                                        </Box>
                                    )}
                                    <ResponsiveSelect
                                        label={t("dashboard.filters.chartMetric")}
                                        value={selectedChartMetric?.field || ""}
                                        onChange={(e) =>
                                            setChartMetric((prev) => ({
                                                ...prev,
                                                [isMakmini ? "makmini" : "akiferra"]: e.target.value,
                                            }))
                                        }
                                        options={chartMetricOptions}
                                        maxWidth={280}
                                        isSmall={isSmall}
                                    />
                                </Box>
                                <MetricTrendChart
                                    ref={chartRef}
                                    labels={chartLabels}
                                    values={chartValues}
                                    seriesLabel={selectedChartMetric?.label || ""}
                                    datasets={chartDatasets}
                                    overlayDatasets={flowOverlayDatasets}
                                    chartType={(isConductivityAB || isWaterTempAB || isTempHumidity) ? "line" : (selectedChartMetric?.aggregation === "sum" || isFlowAB) ? "bar" : "line"}
                                />
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {isHourly
                                            ? `${effectiveStartDate} (${t("dashboard.views.hourly")})`
                                            : `${effectiveStartDate} - ${effectiveEndDate}`}
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 0.5 }}>
                                        <Tooltip title={t("dashboard.exportPNG")}>
                                            <IconButton size="small" onClick={handleExportPNG}>
                                                <ImageIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t("dashboard.exportCSV")}>
                                            <IconButton size="small" onClick={handleExportCSV}>
                                                <DownloadIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Paper>

                            <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", p: 2, mb: 3 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        mb: 1,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        {t("dashboard.sections.topLocations")}
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                        <ResponsiveSelect
                                            label={t("dashboard.filters.topOrderBy")}
                                            maxWidth={280}
                                            value={effectiveTopOrderBy}
                                            onChange={(e) =>
                                                setTopOrderBy((prev) => ({
                                                    ...prev,
                                                    [isMakmini ? "makmini" : "akiferra"]: e.target.value,
                                                }))
                                            }
                                            options={isMakmini ? makminiTopOrderByOptions : akiferraTopOrderByOptions}
                                            isSmall={isSmall}
                                        />
                                        <ResponsiveSelect
                                            label={t("dashboard.filters.sortDirection")}
                                            value={topAsc ? "asc" : "desc"}
                                            onChange={(e) => setTopAsc(e.target.value === "asc")}
                                            options={[
                                                { value: "desc", label: t("dashboard.filters.descending") },
                                                { value: "asc", label: t("dashboard.filters.ascending") },
                                            ]}
                                            isSmall={isSmall}
                                        />
                                        <ResponsiveSelect
                                            label={t("dashboard.filters.limit")}
                                            value={topLimit}
                                            onChange={(e) => setTopLimit(Number(e.target.value))}
                                            options={[
                                                { value: 5, label: "5" },
                                                { value: 10, label: "10" },
                                                { value: 20, label: "20" },
                                            ]}
                                            isSmall={isSmall}
                                        />
                                    </Box>
                                </Box>

                                {topLoading ? (
                                    <Box sx={{ p: 4, textAlign: "center" }}>
                                        <CircularProgress size={28} />
                                    </Box>
                                ) : topError ? (
                                    <Alert severity="error">{topError.message}</Alert>
                                ) : (
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700 }}>
                                                        {t("stats.location")}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }} align="right">
                                                        {metricDefs.find((m) => m.field === topValueField)?.label || t("common.value")}
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {topRows.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={2} align="center">
                                                            {t("dashboard.noData")}
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    topRows.map((row) => (
                                                        <TableRow key={row.location || row.locationName}>
                                                            <TableCell>{row.locationName || "-"}</TableCell>
                                                            <TableCell align="right">{formatter.format(topValueDivider ? numberOrZero(row?.[topValueField]) / topValueDivider : numberOrZero(row?.[topValueField]))}</TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Paper>
                        </>
                    )}
                </Box>
            </Paper>
        </Box>
    );
}
